/**
 * HierarchySelector.js
 *
 * A compound control that lets users:
 *   1. Switch between hierarchy levels (Enterprise / Department / Discipline) via tabs
 *   2. Pick a department (when at Department or Discipline level)
 *   3. Pick a discipline (when at Discipline level)
 *
 * Department admins are constrained to their own department automatically and
 * the Enterprise tab is hidden from them.
 *
 * Props:
 *   compact  - boolean — render a slim single-row version (default false)
 */

import React from 'react';
import { FiGlobe, FiGrid, FiTarget, FiChevronDown, FiX } from 'react-icons/fi';
import { useHierarchy, HIERARCHY_LEVELS, LEVEL_META } from '../context/HierarchyContext';
import useHierarchyAccess from '../hooks/useHierarchyAccess';

// ─── Level tab definitions ────────────────────────────────────────────────────

const LEVEL_ICON_COMPONENTS = {
  enterprise: FiGlobe,
  department: FiGrid,
  discipline: FiTarget,
};

const LEVEL_ICON_INACTIVE_CLASSES = {
  enterprise: 'text-indigo-600',
  department: 'text-sky-600',
  discipline: 'text-teal-600',
};

const LEVEL_ACTIVE_CLASSES = {
  enterprise: 'bg-indigo-600 text-white border-indigo-600',
  department: 'bg-sky-600 text-white border-sky-600',
  discipline: 'bg-teal-600 text-white border-teal-600',
};

const LEVEL_INACTIVE_CLASSES =
  'bg-white text-slate-600 border-slate-300 hover:border-slate-400 hover:text-slate-800';

// ─── Component ────────────────────────────────────────────────────────────────

const HierarchySelector = ({ compact = false }) => {
  const {
    hierarchyLevel,
    selectedDepartment,
    selectedDiscipline,
    departments,
    disciplinesForDepartment,
    lookupsLoading,
    isDeptAdmin,
    ownDepartment,
    setHierarchyLevel,
    setSelectedDepartment,
    setSelectedDiscipline,
    scopeLabel,
  } = useHierarchy();

  const { canView } = useHierarchyAccess();

  const visibleLevels = [
    HIERARCHY_LEVELS.ENTERPRISE,
    HIERARCHY_LEVELS.DEPARTMENT,
    HIERARCHY_LEVELS.DISCIPLINE,
  ].filter((l) => canView(l));

  const activeDepartment = isDeptAdmin ? ownDepartment : selectedDepartment;

  if (compact) {
    return (
      <CompactHierarchySelector
        visibleLevels={visibleLevels}
        hierarchyLevel={hierarchyLevel}
        setHierarchyLevel={setHierarchyLevel}
        activeDepartment={activeDepartment}
        departments={departments}
        disciplinesForDepartment={disciplinesForDepartment}
        selectedDiscipline={selectedDiscipline}
        lookupsLoading={lookupsLoading}
        isDeptAdmin={isDeptAdmin}
        setSelectedDepartment={setSelectedDepartment}
        setSelectedDiscipline={setSelectedDiscipline}
        scopeLabel={scopeLabel}
      />
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 flex flex-wrap items-center gap-2">
      {/* Scope label */}
      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
        Scope:
      </span>

      {/* Level pills (segmented control) */}
      <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5">
        {visibleLevels.map((level) => {
          const isActive = hierarchyLevel === level;
          const Icon = LEVEL_ICON_COMPONENTS[level];
          return (
            <button
              key={level}
              onClick={() => setHierarchyLevel(level)}
              title={LEVEL_META[level].description}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${
                isActive
                  ? LEVEL_ACTIVE_CLASSES[level] + ' shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              <Icon className={isActive ? 'text-white' : LEVEL_ICON_INACTIVE_CLASSES[level]} />
              {LEVEL_META[level].label}
            </button>
          );
        })}
      </div>

      {/* Department dropdown */}
      {(hierarchyLevel === HIERARCHY_LEVELS.DEPARTMENT ||
        hierarchyLevel === HIERARCHY_LEVELS.DISCIPLINE) &&
        (isDeptAdmin ? (
          <span className="text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1">
            {ownDepartment}
          </span>
        ) : (
          <div className="relative">
            <select
              disabled={lookupsLoading}
              value={activeDepartment || ''}
              onChange={(e) => setSelectedDepartment(e.target.value || null)}
              className="appearance-none text-xs border border-slate-300 rounded pl-2 pr-7 py-1 focus:outline-none focus:ring-1 focus:ring-sky-400 bg-white min-w-[140px]"
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.departmentId} value={d.departmentDesc}>
                  {d.departmentDesc}
                </option>
              ))}
            </select>
            <FiChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
          </div>
        ))}

      {/* Discipline dropdown */}
      {hierarchyLevel === HIERARCHY_LEVELS.DISCIPLINE && (
        <div className="relative">
          <select
            disabled={lookupsLoading || !activeDepartment}
            value={selectedDiscipline || ''}
            onChange={(e) => setSelectedDiscipline(e.target.value || null)}
            className="appearance-none text-xs border border-slate-300 rounded pl-2 pr-7 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white disabled:bg-slate-100 disabled:text-slate-400 min-w-[140px]"
          >
            <option value="">All Disciplines</option>
            {disciplinesForDepartment.map((d) => (
              <option key={d.disciplineId} value={d.disciplineDesc}>
                {d.disciplineDesc}
              </option>
            ))}
          </select>
          <FiChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
        </div>
      )}

      {/* Reset button — only when a non-enterprise filter is active */}
      {(hierarchyLevel !== HIERARCHY_LEVELS.ENTERPRISE || activeDepartment || selectedDiscipline) && (
        <button
          onClick={() => {
            setHierarchyLevel(HIERARCHY_LEVELS.ENTERPRISE);
            setSelectedDepartment(null);
            setSelectedDiscipline(null);
          }}
          title="Reset to Enterprise scope"
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition-all"
        >
          <FiX className="text-[11px]" />
          Reset
        </button>
      )}

      {/* Spacer + scope summary */}
      <span className="ml-auto text-[11px] text-slate-500 truncate max-w-xs" title={scopeLabel}>
        {scopeLabel}
      </span>
    </div>
  );
};

// ─── Compact variant (single row, dropdown-style) ────────────────────────────

const CompactHierarchySelector = ({
  visibleLevels,
  hierarchyLevel,
  setHierarchyLevel,
  activeDepartment,
  departments,
  disciplinesForDepartment,
  selectedDiscipline,
  lookupsLoading,
  isDeptAdmin,
  setSelectedDepartment,
  setSelectedDiscipline,
  scopeLabel,
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {/* Level pills */}
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
      {visibleLevels.map((level) => {
        const isActive = hierarchyLevel === level;
        const Icon = LEVEL_ICON_COMPONENTS[level];
        return (
          <button
            key={level}
            onClick={() => setHierarchyLevel(level)}
            title={LEVEL_META[level].description}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
              isActive
                ? LEVEL_ACTIVE_CLASSES[level]
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className={isActive ? 'text-white' : LEVEL_ICON_INACTIVE_CLASSES[level]} />
            <span className="hidden sm:inline">{LEVEL_META[level].label}</span>
          </button>
        );
      })}
    </div>

    {/* Department dropdown (compact) */}
    {(hierarchyLevel === HIERARCHY_LEVELS.DEPARTMENT ||
      hierarchyLevel === HIERARCHY_LEVELS.DISCIPLINE) &&
      !isDeptAdmin && (
        <div className="relative">
          <select
            disabled={lookupsLoading}
            value={activeDepartment || ''}
            onChange={(e) => setSelectedDepartment(e.target.value || null)}
            className="appearance-none text-xs border border-slate-300 rounded-lg pl-2.5 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
          >
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d.departmentId} value={d.departmentDesc}>
                {d.departmentDesc}
              </option>
            ))}
          </select>
          <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
        </div>
      )}

    {/* Discipline dropdown (compact) */}
    {hierarchyLevel === HIERARCHY_LEVELS.DISCIPLINE && (
      <div className="relative">
        <select
          disabled={lookupsLoading || !activeDepartment}
          value={selectedDiscipline || ''}
          onChange={(e) => setSelectedDiscipline(e.target.value || null)}
          className="appearance-none text-xs border border-slate-300 rounded-lg pl-2.5 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white disabled:bg-slate-100 disabled:text-slate-400"
        >
          <option value="">All Disciplines</option>
          {disciplinesForDepartment.map((d) => (
            <option key={d.disciplineId} value={d.disciplineDesc}>
              {d.disciplineDesc}
            </option>
          ))}
        </select>
        <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
      </div>
    )}

    {/* Scope label */}
    <span className="text-xs text-slate-500 hidden lg:inline truncate max-w-xs" title={scopeLabel}>
      {scopeLabel}
    </span>
  </div>
);

export default HierarchySelector;
